import { Box, Button, CircularProgress } from '@mui/material';
import clsx from 'clsx';
import i18n from 'i18next';
import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { I18nextProvider } from 'react-i18next';
import { useTranslation } from 'react-i18next';

import CertificateBack from 'commons/CertificateBack';
import Text from 'commons/Text';
import useOwnedEditions from 'hooks/useOwnedEditions';
import useRole from 'hooks/useRole';
import { ReactComponent as DownloadIcon } from 'images/download.svg';

import CertificateFrontContent from './components/CertificateFrontContent';
import DownloadCertificateLanguageDialog from './components/DownloadCertificateLanguageDialog';
import useStyles from './styles';
import {
  exportCertificatePdf,
  exportCertificatePdfBoth,
  waitForNextPaint,
} from './utils/certificatePdf';

const Certificate = forwardRef(
  (
    {
      artwork,
      certificate,
      isShowButtonDownloadCertificate,
      wrapperStyle,
      wrapperInlineStyle,
      autoOpenDownload,
    },
    ref,
  ) => {
    const classes = useStyles();
    const certificateRef = useRef(null);
    const certificateExportFrontRef = useRef(null);
    const certificateBackRef = useRef(null);
    const certificateExportFrontViRef = useRef(null);
    const certificateBackViRef = useRef(null);
    const certificateExportFrontEnRef = useRef(null);
    const certificateBackEnRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSelectLanguageOpen, setIsSelectLanguageOpen] = useState(false);
    const [downloadLanguage, setDownloadLanguage] = useState('en');
    const [selectedEdition, setSelectedEdition] = useState(null);
    const [exportRenderKey, setExportRenderKey] = useState(0);
    const { t } = useTranslation();

    const ownedEditions = useOwnedEditions(artwork);
    const { isArtist } = useRole();

    const exportI18nRef = useRef(i18n.cloneInstance({ initImmediate: false }));
    const exportI18nViRef = useRef(i18n.cloneInstance({ initImmediate: false }));
    const exportI18nEnRef = useRef(i18n.cloneInstance({ initImmediate: false }));

    useImperativeHandle(ref, () => certificateRef.current);

    // editions array may not be present in all contexts (e.g. certificate?.artwork from list API)
    // When not present, rely solely on isShowButtonDownloadCertificate for access control
    const hasEditionsData = Array.isArray(artwork?.editions) && artwork.editions.length > 0;

    // Editions list to show in the download dialog.
    // Artist → all editions with a certificate; Collector → editions owned by current user.
    // null → dialog hides the edition field (no editions data available in this context).
    const dialogEditions = useMemo(() => {
      if (!hasEditionsData) return null;
      if (isArtist) {
        const list = artwork.editions
          .filter((ed) => ed?.id_certificate !== null)
          .map((ed) => ({ ...ed, total_edition: artwork?.total_edition }));
        return list.length > 0 ? list : null;
      }
      return ownedEditions.map((ed) => ({ ...ed, total_edition: artwork?.total_edition }));
    }, [hasEditionsData, isArtist, artwork, ownedEditions]);

    // Build the certificate to render in the export template, reflecting the chosen edition
    const downloadCertificate = useMemo(() => {
      if (selectedEdition) {
        return { ...certificate, edition_number: selectedEdition.edition_number };
      }
      return certificate;
    }, [selectedEdition, certificate]);

    // Auto-open the download modal when triggered from an external entry point
    useEffect(() => {
      if (!autoOpenDownload) return;
      setDownloadLanguage(i18n.language || 'en');
      setSelectedEdition(dialogEditions?.length === 1 ? dialogEditions[0] : null);
      setIsSelectLanguageOpen(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoOpenDownload]);

    const handleOpenSelectLanguageDialog = () => {
      if (isDownloading) return;
      setDownloadLanguage(i18n.language || 'en');
      setSelectedEdition(dialogEditions?.length === 1 ? dialogEditions[0] : null);
      setIsSelectLanguageOpen(true);
    };

    const handleCloseSelectLanguageDialog = () => {
      if (isDownloading) return;
      setIsSelectLanguageOpen(false);
      setSelectedEdition(null);
    };

    const handleConfirmDownload = async () => {
      if (isDownloading) {
        return;
      }

      setIsDownloading(true);
      setIsSelectLanguageOpen(false);

      try {
        if (downloadLanguage === 'both') {
          await exportI18nViRef.current.changeLanguage('vi');
          await exportI18nEnRef.current.changeLanguage('en');
          setExportRenderKey((prevKey) => prevKey + 1);
          await waitForNextPaint();

          await exportCertificatePdfBoth({
            frontElementVi: certificateExportFrontViRef.current,
            backElementVi: certificateBackViRef.current,
            frontElementEn: certificateExportFrontEnRef.current,
            backElementEn: certificateBackEnRef.current,
            fileName: 'certificate_both',
            ignoreElements: (element) => element.classList.contains(classes.buttonDownload),
          });
          return;
        }

        await exportI18nRef.current.changeLanguage(downloadLanguage);
        setExportRenderKey((prevKey) => prevKey + 1);
        await waitForNextPaint();
        const singleDownloadFileName =
          downloadLanguage === 'vi' ? 'certificate_vn' : 'certificate_en';
        await exportCertificatePdf({
          frontElement: certificateExportFrontRef.current,
          backElement: certificateBackRef.current,
          fileName: singleDownloadFileName,
          ignoreElements: (element) => element.classList.contains(classes.buttonDownload),
        });
      } finally {
        setIsDownloading(false);
      }
    };

    const renderCertificateFrontContent = (
      cert = certificate,
      downloadMode = null,
      language = null,
    ) => (
      <CertificateFrontContent
        artwork={artwork}
        certificate={cert}
        classes={classes}
        downloadMode={downloadMode}
        language={language}
      />
    );

    return (
      <>
        <I18nextProvider
          key={exportRenderKey}
          i18n={exportI18nRef.current}
        >
          <div className={classes.exportWrapper}>
            <Box
              ref={certificateExportFrontRef}
              className={classes.wrapper}
            >
              {renderCertificateFrontContent(
                downloadCertificate,
                downloadLanguage,
                downloadLanguage,
              )}
            </Box>
            <div ref={certificateBackRef}>
              <CertificateBack
                downloadMode={downloadLanguage}
                language={downloadLanguage}
              />
            </div>
          </div>
        </I18nextProvider>

        <I18nextProvider
          key={`vi-${exportRenderKey}`}
          i18n={exportI18nViRef.current}
        >
          <div
            className={classes.exportWrapper}
            style={{ position: 'absolute', left: '-9999px' }}
          >
            <Box
              ref={certificateExportFrontViRef}
              className={classes.wrapper}
            >
              {renderCertificateFrontContent(downloadCertificate, 'both', 'vi')}
            </Box>
            <div ref={certificateBackViRef}>
              <CertificateBack
                downloadMode="both"
                language="vi"
              />
            </div>
          </div>
        </I18nextProvider>

        <I18nextProvider
          key={`en-${exportRenderKey}`}
          i18n={exportI18nEnRef.current}
        >
          <div
            className={classes.exportWrapper}
            style={{ position: 'absolute', left: '-9999px' }}
          >
            <Box
              ref={certificateExportFrontEnRef}
              className={classes.wrapper}
            >
              {renderCertificateFrontContent(downloadCertificate, 'both', 'en')}
            </Box>
            <div ref={certificateBackEnRef}>
              <CertificateBack
                downloadMode="both"
                language="en"
              />
            </div>
          </div>
        </I18nextProvider>

        <Box
          ref={certificateRef}
          className={clsx(classes.wrapper, wrapperStyle)}
          style={wrapperInlineStyle}
        >
          {renderCertificateFrontContent()}

          {isShowButtonDownloadCertificate && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenSelectLanguageDialog}
              disabled={isDownloading}
              className={classes.buttonDownload}
            >
              {isDownloading ? (
                <CircularProgress
                  size={20}
                  color="inherit"
                />
              ) : (
                <DownloadIcon />
              )}
              <Text>{t('download_certificate')}</Text>
            </Button>
          )}
        </Box>

        <DownloadCertificateLanguageDialog
          isOpen={isSelectLanguageOpen}
          selectedLanguage={downloadLanguage}
          selectedEdition={selectedEdition}
          ownedEditions={dialogEditions}
          onClose={handleCloseSelectLanguageDialog}
          onChangeLanguage={setDownloadLanguage}
          onChangeEdition={setSelectedEdition}
          onConfirm={handleConfirmDownload}
          isDownloading={isDownloading}
        />
      </>
    );
  },
);

export default Certificate;
